const PARENT_PIN_HASH_ALGORITHM = "pbkdf2-sha256";
const PARENT_PIN_HASH_VERSION = "v1";

export const PARENT_PIN_HASH_ITERATIONS = 210_000;
export const PARENT_PIN_SALT_BYTES = 16;
export const PARENT_PIN_HASH_BYTES = 32;

export interface ParsedParentPinHash {
  salt: Uint8Array<ArrayBuffer>;
  hash: Uint8Array<ArrayBuffer>;
}

export function encodeParentPinHash(
  salt: Uint8Array<ArrayBuffer>,
  hash: Uint8Array<ArrayBuffer>
): string {
  return [
    PARENT_PIN_HASH_ALGORITHM,
    PARENT_PIN_HASH_VERSION,
    String(PARENT_PIN_HASH_ITERATIONS),
    bytesToBase64(salt),
    bytesToBase64(hash)
  ].join("$");
}

export function parseParentPinHash(
  encodedHash: string
): ParsedParentPinHash | undefined {
  const parts = encodedHash.split("$");
  const algorithm = parts[0];
  const version = parts[1];
  const iterations = parts[2];
  const encodedSalt = parts[3];
  const encodedDerivedHash = parts[4];

  if (
    parts.length !== 5 ||
    algorithm !== PARENT_PIN_HASH_ALGORITHM ||
    version !== PARENT_PIN_HASH_VERSION ||
    iterations !== String(PARENT_PIN_HASH_ITERATIONS) ||
    encodedSalt === undefined ||
    encodedDerivedHash === undefined
  ) {
    return undefined;
  }

  try {
    const salt = base64ToBytes(encodedSalt);
    const hash = base64ToBytes(encodedDerivedHash);

    if (
      salt.length !== PARENT_PIN_SALT_BYTES ||
      hash.length !== PARENT_PIN_HASH_BYTES
    ) {
      return undefined;
    }

    return { salt, hash };
  } catch {
    return undefined;
  }
}

export function isValidParentPinHash(value: string): boolean {
  return parseParentPinHash(value) !== undefined;
}

function bytesToBase64(bytes: Uint8Array<ArrayBuffer>): string {
  return btoa(String.fromCharCode(...bytes));
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
}
